import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { FormFieldRow } from "@/Common/Components/Forms/FormFieldRow";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { SelectKVPControl } from "@/Common/Components/Inputs/SelectKVPControl";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { IMap } from "@/Common/Models/IMap";
import { DropdownItem } from "@/Common/Models/UI/Inputs/DropdownItem";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ValueCodeDropdownItem } from "@/Patient/Components/MedicalItems/Immunization/ValueCodeDropdownItem";
import { ConstantValues } from "../Constants/ConstantValues";
import { ValueSetType } from "../Constants/ValueSetType";
import { CodeValue } from "../Models/CodeValue";
import { RvqDomainValue } from "../Models/RvqDomainValue";
import { VaccinationRefusalModel } from "../Models/VaccinationRefusalModel";
import { QuebecVaccinationRegistryHelper } from "../QuebecVaccinationRegistryHelper";
import { GetValuesRequest } from "../RequestModels/GetValuesRequest";
import { GetVaccinationRefusalResponse } from "../ResponseModels/GetVaccinationRefusalResponse";
import { GetValuesResponse } from "../ResponseModels/GetValuesResponse";
import { BaseImmunizationProfileSection, BaseImmunizationProfileSectionState, IBaseImmunizationProfileSectionProps } from "./BaseImmunizationProfileSection";
import { CodeValueSearchAutocompleteCustom } from "./CodeValueSearchAutocompleteCustom";

interface IImmunizationRefusalProps extends IBaseImmunizationProfileSectionProps {
    addVaccinationRefusals: (model: VaccinationRefusalModel[]) => void;
    patientDateOfBirth: Date | null;
    currentVaccinationRefusal: VaccinationRefusalModel;
}

class ImmunizationRefusalState extends BaseImmunizationProfileSectionState {
    public isAllAntigensSelected: boolean;
    public agentOptions: KeyValuePair<string, string>[];
    public availableAntigens: DropdownItem<CodeValue>[];
    public reasonOptions: IMap<RvqDomainValue>;
    public selectedAntigenCodes: CodeValue[];
    public selectedAgentCode: string;
    public reason: string;
    public currentVaccinationRefusal: VaccinationRefusalModel;

    constructor(init?: Partial<ImmunizationRefusalState>) {
        super(init);
    }

    public static default(): ImmunizationRefusalState {
        return new ImmunizationRefusalState({
            ...BaseImmunizationProfileSectionState.default(),
            isAllAntigensSelected: false,
            agentOptions: [],
            reasonOptions: {},
            availableAntigens: [],
            selectedAntigenCodes: [],
            reason: "",
            currentVaccinationRefusal: null
        });
    }
}

class ImmunizationRefusal extends BaseImmunizationProfileSection<ImmunizationRefusalState, IImmunizationRefusalProps> {
    public constructor(props: IImmunizationRefusalProps) {
        super(props);

        const initialState = ImmunizationRefusalState.default();
        initialState.currentVaccinationRefusal = new VaccinationRefusalModel(props.currentVaccinationRefusal);

        this.state = initialState;
    }

    protected requestValues = new GetValuesRequest({
        Type: [ValueSetType.VaccinationRefusalMotive, ValueSetType.Agent]
    });

    public componentDidMount(): void {
        super.componentDidMount(
            () => {
                this.mutateState(s => {
                    s.agentOptions = KeyValuePair.fromMapOfStrings(s.values?.Types[ValueSetType.Agent]) || [];
                    s.availableAntigens = [];
                    s.reasonOptions = s.values?.Types[ValueSetType.VaccinationRefusalMotive.toString()] ?? {};

                    s.reason = PatientHelper.getPatientIsOfAgeOfConsent(this.props.patientDateOfBirth)
                        ? ConstantValues.PatientRefusalReasonCode
                        : ConstantValues.LegalRepresentativeRefusal;
                });
            }
        );
    }

    protected getBody(): JSX.Element {
        return (
            <div>
                <div style={this.isNew ? { marginBottom: "20px" } : {}}>
                    {this.isNew && (
                        <FormFieldRow
                            labelValueKey=""
                            isEditMode={this.isNew}
                        >
                            <CheckboxControl
                                checked={this.state.isAllAntigensSelected}
                                label={cacheStore.GetString("QuebecVaccinationRegistry_RefusalAllAntigensLabel")}
                                onChange={checked => this.mutateState(s => {
                                    s.isAllAntigensSelected = checked;
                                    s.selectedAntigenCodes = [];
                                    s.availableAntigens = [];
                                    s.selectedAgentCode = "";
                                })}
                            />
                        </FormFieldRow>
                    )}
                    {(!this.state.isAllAntigensSelected && this.isNew) && (
                        <FormFieldRow
                            labelValueKey="QuebecVaccinationRegistry_ImmunizingAgentLabel"
                            isEditMode={this.isNew}
                        >
                            <SelectKVPControl
                                Callback={item => this.mutateState(
                                    s => {
                                        s.selectedAgentCode = item;
                                        s.selectedAntigenCodes = [];
                                        s.availableAntigens = [];
                                    },
                                    async () => {
                                        const response = await Client.postAsync<GetValuesResponse>(
                                            ServerOperations.Server.QuebecVaccinationRegistry_GetValues,
                                            new GetValuesRequest({ AgentCode: item, Type: [] })
                                        );
                                        this.mutateState(s => {
                                            const codeValues = Object
                                                .keys(response.data?.Antigens || [])
                                                .map(key => new CodeValue({ Code: key, Label: response.data.Antigens[key]?.Label }));
                                            s.availableAntigens = codeValues
                                                .map(codeValue => new ValueCodeDropdownItem({ Item: codeValue, Active: false }));

                                            if (codeValues.length == 1)
                                                s.selectedAntigenCodes = [s.availableAntigens[0].Item];
                                        });
                                    }
                                )}
                                AvailableValues={this.state.agentOptions}
                                Value={this.state.selectedAgentCode}
                                IncludeEmptyChoice
                                isInvalid={!this.state.selectedAgentCode}
                            />
                        </FormFieldRow>
                    )}
                    {(!this.state.isAllAntigensSelected) && (
                        <FormFieldRow
                            labelValueKey={this.isNew ? "QuebecVaccinationRegistry_AntigensLabel" : "QuebecVaccinationRegistry_AntigenImmunizingAgentLabel"}
                            isEditMode={this.isNew}
                            value={this.isViewing ? this.getAntigenString() : "--"}
                        >
                            <CodeValueSearchAutocompleteCustom
                                dropDownListMaxHeight={165}
                                componentId="add_antigens_vaccinationRefusal"
                                selectedItemList={this.state.selectedAntigenCodes
                                    .map(antigen => new CodeValue({
                                        Code: antigen.Code,
                                        Label: QuebecVaccinationRegistryHelper.translateValueSetString(antigen, ValueSetType.Antigen)
                                    }))
                                    ?.toImmutable()
                                }
                                onSearch={async text => {
                                    const items = this.state.availableAntigens.filter(x => x.Item.Label.contains(text, { ignoreCase: true, ignoreDiacritics: true }));
                                    return items.length > 1
                                        ? [
                                            new ValueCodeDropdownItem({ Item: new CodeValue({ Code: "SELECTALL", Label: cacheStore.GetString("SelectAllLabel") }) }),
                                            ...items
                                        ]
                                        : items;
                                }}
                                disableInputField={this.state.availableAntigens.length <= this.state.selectedAntigenCodes.length}
                                closeDropDownAfterSelection
                                allowMultipleItemSelection
                                valid={this.state.selectedAntigenCodes?.any()}
                                onClearCallback={(value: ValueCodeDropdownItem) => this.removeAntigen(value)}
                                onSelectionCallback={(value: ValueCodeDropdownItem) => this.onSelectAntigen(value)}
                            />
                        </FormFieldRow>
                    )}
                </div>
                <FormFieldRow
                    labelValueKey="ReasonLabel"
                    isEditMode={this.isNew}
                    value={this.isViewing ? this.getReasonString() : "--"}
                >
                    <SelectKVPControl
                        Callback={item => this.mutateState(s => s.reason = item)}
                        AvailableValues={this.refusalMotiveReasons}
                        Value={this.state.reason}
                        IncludeEmptyChoice
                        isInvalid={!this.state.reason?.trim()}
                    />
                </FormFieldRow>
                {this.isViewing && (
                    <div>
                        <label className="bold">{cacheStore.GetString("QuebecVaccinationRegistry_ApplicationDateLabel")}</label>
                        <div>
                            <FormFieldRow
                                isEditMode={false}
                                value={this.formatDateForMoment(this.state.currentVaccinationRefusal?.DatePeriod?.Start)}
                                labelValueKey="DuLabel"
                                style={{ width: "260px", display: "inline-block" }}
                                inline
                                disableBoldLabel
                                valueStyle={{ marginTop: "10px" }}
                            />
                            <FormFieldRow
                                isEditMode={false}
                                value={this.formatDateForMoment(this.state.currentVaccinationRefusal?.DatePeriod?.End)}
                                labelValueKey="AuLabel"
                                style={{ marginLeft: "10px", width: "260px", display: "inline-block" }}
                                inline
                                disableBoldLabel
                                valueStyle={{ marginTop: "10px" }}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private get refusalMotiveReasons(): KeyValuePair<string, string>[] {
        return KeyValuePair.fromMapOfStrings(QuebecVaccinationRegistryHelper.getTranslatedDictionary(this.state.reasonOptions, ValueSetType.VaccinationRefusalMotive));
    }

    private getReasonString(): string {
        return QuebecVaccinationRegistryHelper
            .translateValueSet(
                this.state.currentVaccinationRefusal?.Reason?.Code,
                this.state.reasonOptions?.[this.state.currentVaccinationRefusal?.Reason?.Code]?.Label ?? "--",
                ValueSetType.VaccinationRefusalMotive
            );
    }

    private getAntigenString(): string {
        return (this.state.currentVaccinationRefusal?.Vaccine?.Code === ConstantValues.AllVaccinations)
            ? cacheStore.GetString("QuebecVaccinationRegistry_Refusal_AllVaccines")
            : QuebecVaccinationRegistryHelper.translateValueSetString(
                this.state.currentVaccinationRefusal?.Vaccine,
                ValueSetType.Antigen
            );
    }

    protected async onSave(): Promise<void> {
        let vaccinationRefusalResponseList: VaccinationRefusalModel[] = [];
        let errors: ErrorMessage[] = [];

        if (!this.state.isAllAntigensSelected) {
            for (let antigen of this.state.selectedAntigenCodes) {
                const response = await Client.postAsync<GetVaccinationRefusalResponse>(
                    ServerOperations.Server.QuebecVaccinationRegistry_AddVaccinationRefusal,
                    {
                        PatientId: this.props.patientId,
                        Reason: this.state.reason,
                        AntigenCode: antigen.Code,
                        TransactionId: this.state.cachedTransactionId
                    }
                );

                this.mutateState(s => s.cachedTransactionId = response?.data?.TransactionId);
                if (response.isSuccess)
                    vaccinationRefusalResponseList.push(response.data?.VaccinationRefusal);
                else
                    errors = errors.concat(response.errors);
            }
        }
        else {
            const response = await Client.postAsync<GetVaccinationRefusalResponse>(
                ServerOperations.Server.QuebecVaccinationRegistry_AddAllAntigensVaccinationRefusal,
                {
                    PatientId: this.props.patientId,
                    Reason: this.state.reason,
                    TransactionId: this.state.cachedTransactionId
                }
            );
            this.mutateState(s => s.cachedTransactionId = response?.data?.TransactionId);
            if (response.isSuccess)
                vaccinationRefusalResponseList.push(response.data?.VaccinationRefusal);
            else
                errors = errors.concat(response.errors);
        }

        if (vaccinationRefusalResponseList.any()) {
            this.props.addVaccinationRefusals(vaccinationRefusalResponseList);
            this.props.onClose();
        }
        if (errors.any())
            sessionStore.showPopup(errors);
    }

    protected canSave(): boolean {
        return this.state.reason
            && (this.state.isAllAntigensSelected || this.state.selectedAntigenCodes?.any());
    }

    private removeAntigen(item: ValueCodeDropdownItem): void {
        if (!item || !item.Item)
            return;

        this.mutateState(s =>
            s.selectedAntigenCodes = s.selectedAntigenCodes.filter(x => x.Code != item.Item.Code)
        );
    }

    private onSelectAntigen(item: ValueCodeDropdownItem): void {
        if (!item || !item.Item)
            return;

        if (item.Item.Code === "SELECTALL") {
            this.mutateState(s => s.selectedAntigenCodes = this.state.availableAntigens.map(x => x.Item));
            return;
        }

        this.mutateState(s => {
            if (s.selectedAntigenCodes.indexOf(item.Item) == -1)
                s.selectedAntigenCodes.push(item.Item);
        });
    }
}

export { ImmunizationRefusal };
